import type { Theme } from '@split/shared';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

export const THEME_STORAGE_KEY = 'split.theme';
const THEME_COLOR = { light: '#FBF6EE', dark: '#1B1612' } as const;

const listeners = new Set<() => void>();

export function getThemePreference(): Theme {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // ignore
  }
  return 'system';
}

function systemDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Sets `<html data-theme>` and the `theme-color` meta for the resolved theme (DESIGN 2.5). */
export function applyTheme(pref: Theme = getThemePreference()): 'light' | 'dark' {
  const resolved = pref === 'dark' || (pref === 'system' && systemDark()) ? 'dark' : 'light';
  document.documentElement.dataset.theme = resolved;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[resolved]);
  return resolved;
}

export function setThemePreference(pref: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // ignore
  }
  applyTheme(pref);
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const theme = useSyncExternalStore<Theme>(subscribe, getThemePreference, () => 'system');
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);
  return [theme, useCallback((t: Theme) => setThemePreference(t), [])];
}
